import { useTranslation } from 'react-i18next';

import Button from '../ui/Button';

interface EmptyProfilesStateProps {
  type: 'child' | 'adult';
  onCreateProfile: () => void;
  onAddTemporary: () => void;
}

/**
 * Empty state component shown when user has no saved profiles
 * Guides users to create a profile or add a temporary participant
 */
export default function EmptyProfilesState({
  type,
  onCreateProfile,
  onAddTemporary,
}: EmptyProfilesStateProps) {
  const { t } = useTranslation('booking');
  const typeLabel =
    type === 'child' ? t('components.emptyProfiles.child') : t('components.emptyProfiles.adult');
  return (
    <div className="p-6 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
      <div className="text-center">
        {/* Info Icon */}
        <div className="flex justify-center mb-3">
          <svg
            className="w-12 h-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>

        {/* Message */}
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {t('components.emptyProfiles.title', { type: typeLabel })}
        </h3>
        <p className="text-sm text-gray-600 mb-6">{t('components.emptyProfiles.message')}</p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button variant="primary" onClick={onCreateProfile}>
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            {t('components.emptyProfiles.createProfile', { type: typeLabel })}
          </Button>

          <Button variant="outline" onClick={onAddTemporary}>
            {t('components.emptyProfiles.addTemporary')}
          </Button>
        </div>
      </div>
    </div>
  );
}
